import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '../../components/ui/use-toast';
import { useT } from '../../context/LanguageContext';
import { Plus, Edit, Trash2, Eye, BookOpen, ArrowLeft, MapPin, Clock } from 'lucide-react';
import { getAllMenus, deleteMenu } from '../../services/menuService';
import { confirm } from '../../components/ui/confirm-dialog';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { getRestaurantFromStorage } from '../../services/restaurant';

export default function MenuList({ restaurantId: propRestaurantId }) {
  const navigate = useNavigate();
  const { restaurantId: urlRestaurantId, locationId } = useParams();
  const { restaurantId: contextRestaurantId } = useRestaurant();
  const { user } = useAuth();
  
  // Get restaurant from localStorage
  const storedRestaurant = getRestaurantFromStorage();
  const storedRestaurantId = storedRestaurant?.id;
  
  // Get restaurant from logged-in user
  const userRestaurantId = user?.restaurant_id || user?.restaurant;
  
  // Use prop if provided, otherwise use URL param, otherwise use context, otherwise use user, otherwise use localStorage
  const restaurantId = propRestaurantId || urlRestaurantId || contextRestaurantId || userRestaurantId || storedRestaurantId;
  
  console.log('MenuList - Restaurant ID Sources:', {
    prop: propRestaurantId,
    url: urlRestaurantId,
    context: contextRestaurantId,
    user: userRestaurantId,
    stored: storedRestaurantId,
    final: restaurantId
  });
  
  const { toast } = useToast();
  // As in MenuForm: the kiosk badge is translated, the rest of this screen
  // is still hardcoded English and is its own piece of work.
  const { t: tr } = useT();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationName, setLocationName] = useState('');
  // Active / Inactive / All filter for the list (client-side).
  const [statusFilter, setStatusFilter] = useState('all');
  // Service-style filter: all / à la carte / buffet.
  const [typeFilter, setTypeFilter] = useState('all');

  // Base path for this location's menus — every link hangs off it.
  const menusBase = `/restaurant/${restaurantId}/locations/${locationId}/menus`;

  const loadMenus = useCallback(async () => {
    if (!restaurantId || !locationId) {
      console.warn('MenuList: missing restaurantId/locationId');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // One list API returns every menu; keep just this location's, since the builder is location-first.
      const data = await getAllMenus(restaurantId);
      const lid = Number(locationId);
      const menuArray = (Array.isArray(data) ? data : []).filter(
        (m) => (m.location_ids || []).includes(lid),
      );
      setMenus(menuArray);
      // Find this branch's name for the header from any menu's `locations`.
      const match = menuArray
        .flatMap((m) => m.locations || [])
        .find((l) => l.id === lid);
      if (match?.name) setLocationName(match.name);
    } catch (error) {
      console.error('MenuList: Error loading menus:', error);
      setMenus([]);
      toast({
        title: 'Error',
        description: error.error || error.message || 'Failed to load menus',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, locationId, toast]);

  useEffect(() => {
    if (restaurantId && locationId) {
      loadMenus();
    }
  }, [restaurantId, locationId, loadMenus]);

  const handleDelete = async (menuId, menuName) => {
    if (!(await confirm({ title: 'Delete menu?', description: `Delete "${menuName}"? This will also delete all sections and items.`, confirmText: 'Delete menu', tone: 'destructive' }))) {
      return;
    }
    try {
      await deleteMenu(restaurantId, menuId);
      toast({ title: 'Success', description: 'Menu deleted successfully' });
      loadMenus();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete menu',
        variant: 'destructive',
      });
    }
  };

  if (!restaurantId) {
    return (
      <div className="p-6 w-full">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500 text-lg mb-4">
              No restaurant selected. Please select a restaurant first.
            </p>
            <p className="text-sm text-gray-400 mb-4">
              Debug Info: {JSON.stringify({
                userRestaurantId: user?.restaurant_id || user?.restaurant,
                storedRestaurantId: getRestaurantFromStorage()?.id,
                userEmail: user?.email
              }, null, 2)}
            </p>
            <Button 
              onClick={() => navigate('/restaurant-setup')}
            >
              Go to Restaurant Setup
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 w-full">
      {/* Header */}
      <button
        type="button"
        onClick={() => navigate(`/restaurant/${restaurantId}/menus`)}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        All locations
      </button>
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="font-display text-4xl font-semibold text-foreground tracking-tight leading-none">Menus</h1>
          {locationName && (
            <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              {locationName}
            </p>
          )}
        </div>
        <Button onClick={() => navigate(`${menusBase}/new`)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Menu
        </Button>
      </div>

      {/* Active / Inactive filter */}
      {!loading && menus.length > 0 && (
        <div className="inline-flex rounded-lg border border-border bg-secondary/40 p-0.5 mb-5">
          {['all', 'active', 'inactive'].map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setStatusFilter(key)}
              className={`px-3 py-1.5 text-sm rounded-md capitalize transition-colors ${
                statusFilter === key
                  ? 'bg-background shadow-sm text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {key}
            </button>
          ))}
        </div>
      )}

      {/* À la carte / Buffet filter */}
      {!loading && menus.length > 0 && (
        <div className="inline-flex rounded-lg border border-border bg-secondary/40 p-0.5 mb-5 ml-2">
          {[['all', 'All types'], ['ALACARTE', 'À la carte'], ['BUFFET', 'Buffet']].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTypeFilter(key)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                typeFilter === key
                  ? 'bg-background shadow-sm text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading menus…</div>
      ) : menus.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="font-display text-xl text-foreground mb-2">No menus yet</h3>
            <p className="text-muted-foreground mb-4">Create your first menu to get started</p>
            <Button onClick={() => navigate(`${menusBase}/new`)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Menu
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {menus.filter((m) =>
            statusFilter === 'all'
              ? true
              : statusFilter === 'active'
                ? m.is_active
                : !m.is_active,
          ).filter((m) =>
            typeFilter === 'all'
              ? true
              : (m.menu_type || 'ALACARTE') === typeFilter,
          ).map((menu) => (
            <Card key={menu.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-display text-lg font-semibold text-foreground tracking-tight">{menu.name}</h3>
                      <span className={`px-2 py-0.5 text-[11px] rounded-full font-medium ${
                        menu.is_active
                          ? 'bg-success-soft text-success'
                          : 'bg-secondary text-muted-foreground'
                      }`}>
                        {menu.is_active ? '● Active' : 'Inactive'}
                      </span>
                      <span className={`px-2 py-0.5 text-[11px] rounded-full font-medium ${
                        menu.menu_type === 'BUFFET'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-secondary text-muted-foreground'
                      }`}>
                        {menu.menu_type === 'BUFFET' ? 'Buffet' : 'À la carte'}
                      </span>
                      {/* Only flagged when OFF: most menus are published, so a badge on every row would say nothing. */}
                      {menu.show_on_kiosk === false && (
                        <span className="inline-flex items-center rounded-full border border-slate-200
                                         bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">
                          {tr('menus.kiosk.notOn')}
                        </span>
                      )}
                      {menu.show_on_website === false && (
                        <span
                          className="px-2 py-0.5 text-[11px] rounded-full font-medium bg-secondary text-muted-foreground"
                          title="Hidden from the public website. Still served on the QR menu and the order screen."
                        >
                          Not on website
                        </span>
                      )}
                      {!menu.is_active && menu.deactivated_at && (
                        <span className="text-[11px] text-muted-foreground/80">
                          since {new Date(menu.deactivated_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {menu.description && (
                      <p className="text-sm text-muted-foreground mt-1">{menu.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground/80">
                      <span className="font-mono tabular">
                        {menu.section_count || 0} section(s)
                      </span>
                      {Array.isArray(menu.locations) && menu.locations.length > 1 && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {menu.locations.length} locations
                        </span>
                      )}
                      {menu.follow_location_hours ? (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Location hours
                        </span>
                      ) : (menu.active_from || menu.active_until) && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {menu.active_from || '--:--'}–{menu.active_until || '--:--'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`${menusBase}/${menu.id}`)}
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`${menusBase}/${menu.id}/edit`)}
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(menu.id, menu.name)}
                      title="Delete"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
